"use client";

import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ChevronDown } from "lucide-react";

export default function Home() {
  const handleScrollToCategories = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: "smooth",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-16 sm:py-24 lg:py-32 min-h-screen flex items-center relative overflow-hidden pt-24">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 sm:-top-20 -right-10 sm:-right-20 w-40 sm:w-60 lg:w-80 h-40 sm:h-60 lg:h-80 bg-purple-200 rounded-full opacity-20 animate-pulse"></div>
          <div
            className="absolute -bottom-16 sm:-bottom-32 -left-16 sm:-left-32 w-48 sm:w-72 lg:w-96 h-48 sm:h-72 lg:h-96 bg-indigo-200 rounded-full opacity-15 animate-bounce"
            style={{ animationDuration: "3s" }}
          ></div>
          <div className="absolute top-1/2 left-1/4 w-2 sm:w-3 lg:w-4 h-2 sm:h-3 lg:h-4 bg-purple-400 rounded-full animate-ping"></div>
          <div
            className="absolute top-1/3 right-1/3 w-2 sm:w-3 h-2 sm:h-3 bg-indigo-400 rounded-full animate-ping"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute bottom-1/3 right-1/4 w-3 sm:w-4 lg:w-5 h-3 sm:h-4 lg:h-5 bg-blue-400 rounded-full animate-ping"
            style={{ animationDelay: "2s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 sm:mb-8 leading-tight">
              Expert <span className="text-purple-600">Accounting</span>
              <br />
              Mentors
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed px-4">
              Connect your business with experienced accounting professionals
              and mentors. Get the expertise you need to grow your business with
              confidence and achieve financial success.
            </p>

            {/* Dynamic Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 max-w-4xl mx-auto px-4">
              <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">
                  24/7 Available
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Book sessions anytime, anywhere
                </p>
              </div>

              <div
                className="bg-white/80 backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                style={{ animationDelay: "0.2s" }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">
                  Verified Experts
                </h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  All mentors are certified professionals
                </p>
              </div>

              <div
                className="bg-white/80 backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 sm:col-span-2 lg:col-span-1"
                style={{ animationDelay: "0.4s" }}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">
                  Instant Results
                </h3>
                <p className="text-sm text-gray-600">
                  See immediate improvements in your business
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 px-4">
              <Link
                href="/booking"
                className="btn-primary text-lg sm:text-xl px-8 sm:px-10 py-4 sm:py-5 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Find a Mentor Now
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 ml-2 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
              <a
                href="#services"
                className="btn-secondary text-lg sm:text-xl px-8 sm:px-10 py-4 sm:py-5 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Learn More
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5 ml-2 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Jumping Down Arrow Animation */}
        <div className="hidden md:flex absolute bottom-8 left-0 right-0 z-20 justify-center">
          <button
            onClick={handleScrollToCategories}
            className="flex flex-col items-center space-y-2 animate-bounce-slow cursor-pointer hover:opacity-80 transition-opacity focus:outline-none"
            aria-label="Scroll naar beneden"
          >
            <div className="w-6 h-10 border-2 border-purple-600 rounded-full flex items-start justify-center p-1">
              <div className="w-1 h-2 bg-purple-600 rounded-full animate-scroll-dot" />
            </div>
            <ChevronDown className="w-4 h-4 text-purple-600" strokeWidth={2} />
          </button>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="py-12 sm:py-16 lg:py-20 scroll-mt-16 mt-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Our <span className="text-purple-600">Services</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 px-4">
              Expert guidance tailored to your business needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow flex flex-col h-full">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                Financial Planning
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed flex-grow">
                Strategic financial guidance to help your business thrive and
                make informed decisions.
              </p>
              <Link
                href="/booking"
                className="btn-secondary w-full text-sm sm:text-base mt-auto"
              >
                Book Session
              </Link>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 hover:shadow-xl transition-shadow flex flex-col h-full">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Tax Optimization
              </h3>
              <p className="text-gray-600 mb-6 flex-grow">
                Maximize your tax efficiency with expert advice from certified
                professionals.
              </p>
              <Link href="/booking" className="btn-secondary w-full mt-auto">
                Book Session
              </Link>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow flex flex-col h-full">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Business Growth
              </h3>
              <p className="text-gray-600 mb-6 flex-grow">
                Scale your business with mentorship from successful
                entrepreneurs and advisors.
              </p>
              <Link href="/booking" className="btn-secondary w-full mt-auto">
                Book Session
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50 scroll-mt-16 mb-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It <span className="text-purple-600">Works</span>
            </h2>
            <p className="text-xl text-gray-600">
              Simple steps to connect with your perfect mentor
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Choose Your Expert
              </h3>
              <p className="text-gray-600">
                Browse our network of verified accounting professionals and
                mentors.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Book Your Session
              </h3>
              <p className="text-gray-600">
                Schedule a convenient time that works for both you and your
                chosen expert.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Get Expert Guidance
              </h3>
              <p className="text-gray-600">
                Receive personalized advice and solutions for your business
                challenges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-32 bg-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-purple-100 mb-12">
            Join thousands of businesses that trust our experts
          </p>
          <Link
            href="/booking"
            className="bg-white text-purple-600 font-bold py-4 px-8 rounded-lg text-lg hover:bg-gray-100 transition-colors"
          >
            Get Started Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
